import type { Map as MlMap } from 'maplibre-gl';

/**
 * INDICATORS — what the map is allowed to draw.
 *
 * The model is a trading chart. The map is the chart; each indicator is an
 * overlay switched on over the top of it, independently, without touching the
 * thing underneath. That is also exactly why they are legal on the CarPlay
 * screen: every one of them is MAP CONTENT drawn into the base view — the
 * corridor, the cart paths, the hazards — and not a panel, card or control
 * floated on top of it. Apple's rule bans the second kind, not the first.
 *
 * Each one is a named group of MapLibre layers rather than a prop threaded
 * through the component tree. That matters because the same map now renders on
 * four surfaces (phone, head unit, desktop, CarPlay) and a per-surface prop for
 * every drawable would be twenty-odd props that inevitably drift apart. A
 * settings object and a visibility sweep cannot drift: every surface reads the
 * same keys and gets the same map.
 *
 * 🟥 CARPLAY IS THE REASON THIS EXISTS. The car screen takes no touch, so a
 * golfer cannot turn anything off there — whatever the phone says is what the
 * car draws. Indicators are set once on the phone and obeyed everywhere.
 */
export interface Indicators {
  /** Blue outline showing where the hole starts and ends. */
  corridor: boolean;
  /** Dim everything that is not the hole being played. */
  focus: boolean;
  /** Red buggy path network. */
  cartPaths: boolean;
  /** Bunkers and water carries. */
  hazards: boolean;
  /** Keep-off zones — trees, gardens, protected turf. */
  keepOut: boolean;
  /** Green ring and front/middle/back markers. */
  greens: boolean;
  /** Tee and green end markers with labels. */
  ends: boolean;
  /** Aim line and target dot. */
  aim: boolean;
  /** Other buggies on the course. */
  fleet: boolean;
  /** Routed line to the next tee. */
  route: boolean;
}

export const INDICATOR_DEFAULTS: Indicators = {
  corridor: true,
  focus: true,
  cartPaths: true,
  hazards: true,
  keepOut: true,
  greens: true,
  ends: true,
  aim: true,
  // Off by default: customers own one cart, so there is nothing to show.
  fleet: false,
  route: true,
};

/** Human labels, in the order they should be listed. */
export const INDICATOR_LABELS: { key: keyof Indicators; label: string; hint: string }[] = [
  { key: 'corridor', label: 'Hole corridor', hint: 'Where the hole starts and ends' },
  { key: 'focus', label: 'Dim other holes', hint: 'Black out everything off this hole' },
  { key: 'cartPaths', label: 'Cart paths', hint: 'The buggy path network' },
  { key: 'hazards', label: 'Hazards', hint: 'Bunkers and water' },
  { key: 'keepOut', label: 'Keep-off zones', hint: 'Trees, gardens, protected turf' },
  { key: 'greens', label: 'Greens', hint: 'Green shape and F/M/B markers' },
  { key: 'ends', label: 'Tee and green markers', hint: 'Labelled rings at each end' },
  { key: 'aim', label: 'Aim line', hint: 'Your target line' },
  { key: 'fleet', label: 'Other buggies', hint: 'Live fleet positions' },
  { key: 'route', label: 'Route to next tee', hint: 'Along the cart paths' },
];

/** Which MapLibre layers each indicator owns. */
const LAYERS: Record<keyof Indicators, string[]> = {
  corridor: ['corridor-fill', 'corridor-line', 'hole-line'],
  focus: ['mask-far', 'mask-near'],
  cartPaths: ['cart-path'],
  hazards: ['hazard-dot', 'hazard-label'],
  keepOut: ['keepout-fill', 'keepout-line'],
  greens: ['green-fill', 'green-line'],
  ends: ['ends-ring', 'ends-label'],
  aim: ['aim-line', 'aim-dot'],
  fleet: ['fleet-dot', 'fleet-label'],
  route: ['route-casing', 'route-line'],
};

/**
 * Apply indicators to a live map.
 *
 * Silently skips layers that are not present: the style is built once at load
 * and some layers only exist when their data does, so a missing layer is normal
 * rather than an error worth throwing over.
 */
export function applyIndicators(m: MlMap, ind: Indicators): void {
  for (const [key, ids] of Object.entries(LAYERS) as [keyof Indicators, string[]][]) {
    const on = ind[key] !== false;
    for (const id of ids) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
    }
  }
}
